import { Fragment, useState, useEffect, useMemo } from 'react';
import { Dialog, Popover, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  ShoppingCartIcon,
  GlobeAltIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';
import Imgix from 'react-imgix';
import ImgixMedia from './ImgixMedia';
import { products } from '../data/products';
import { heroParamsCode } from '../data/heroParamsCode';
import ProductGrid from './ProductGrid';
import ImageUrlDrawer from './ImageUrlDrawer';
import { ImageSet } from './ImageUrlEditor';
import FileSizeOverlay from './FileSizeOverlay';
import storageService from '../utils/storageService';

const navigation = {
  mainLinks: [
    { name: 'Customers', href: '#' },
    { name: 'Docs', href: 'https://docs.imgix.com/' },
    { name: 'GitHub', href: 'https://github.com/imgix' }
  ],
  solutions: [
    { name: 'Faster Webpages', href: '#', description: 'Optimize your images for web performance' },
    { name: 'eCommerce', href: '#', description: 'Product images that convert' },
    { name: 'Digital Asset Management', href: '#', description: 'Manage your visual content' },
    { name: 'Unleashed Creativity', href: '#', description: 'Create stunning visuals with AI' }
  ],
  products: [
    { name: 'Setup Guide', href: '#' },
    { name: 'Documentation', href: 'https://docs.imgix.com/' },
    { name: 'API Reference', href: '#' },
    { name: 'Libraries & SDKs', href: '#' }
  ],
  company: [
    { name: 'About Us', href: '#' },
    { name: 'Blog', href: '#' },
    { name: 'Careers', href: '#' },
    { name: 'Events', href: '#' }
  ]
};

const footerNavigation = {
  links: [
    { name: 'Docs', href: 'https://docs.imgix.com/' },
    { name: 'GitHub', href: 'https://github.com/imgix' }
  ],
  company: [
    { name: 'Who we are', href: '#' },
    { name: 'Press', href: '#' }
  ],
  contact: [{ name: 'Contact', href: '#' }]
};

export default function Example() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language, toggleLanguage } = useLanguage();

  // ヒーロー画像用商品を抽出
  const heroProduct = products.find((product) => product.isHero);
  
  // Original URLs that will be used for reset
  const originalHeroUrl = heroProduct?.imageSrc || '';
  const originalProductUrls = products.filter(product => !product.isHero).map(product => product.imageSrc);
  
  // Store the original params for reset
  const originalHeroParams = useMemo(() => {
    return heroProduct?.params || {};
  }, [heroProduct]); // Only recompute if heroProduct changes
  
  const originalProductParams = useMemo(() => {
    return {};
  }, []); // Empty dependency array means this will only be calculated once
  
  // Create a special "original" image set ID
  const ORIGINAL_SET_ID = "original";
  
  // Track current active image set ID in state
  const [currentImageSetId, setCurrentImageSetId] = useState<string | null>(() => {
    return storageService.getCurrentImageSetId();
  });
  
  // Initialize hero parameters based on current image set if available
  const [heroImgixParams, setHeroImgixParams] = useState(() => {
    const currentSetId = storageService.getCurrentImageSetId();
    
    // If there's a current set ID, try to load its specific parameters
    if (currentSetId && currentSetId !== ORIGINAL_SET_ID) {
      const setSpecificParams = storageService.getHeroParamsForSet(currentSetId);
      if (setSpecificParams) {
        return setSpecificParams;
      }
    }
    
    // Fall back to global parameters or defaults
    const savedHeroParams = storageService.getHeroImgixParams();
    return savedHeroParams || {...originalHeroParams};
  });
  
  // Initialize product parameters based on current image set if available
  const [imgixParams, setImgixParams] = useState(() => {
    const currentSetId = storageService.getCurrentImageSetId();
    
    // If there's a current set ID, try to load its specific parameters
    if (currentSetId && currentSetId !== ORIGINAL_SET_ID) {
      const setSpecificParams = storageService.getProductParamsForSet(currentSetId);
      if (setSpecificParams) {
        return setSpecificParams;
      }
    }
    
    // Fall back to global parameters or defaults
    const savedProductParams = storageService.getProductImgixParams();
    return savedProductParams || {...originalProductParams};
  });
  
  // Initialize hero image URL from localStorage or fallback to the original
  const [heroImageUrl, setHeroImageUrl] = useState<string>(() => {
    const savedHeroUrl = storageService.getHeroImageUrl();
    return savedHeroUrl || originalHeroUrl;
  });

  // 通常の商品だけ
  const regularProducts = products.filter((product) => !product.isHero);
  
  // Initialize product image URLs from localStorage or fallback to the originals
  const [productImageUrls, setProductImageUrls] = useState<string[]>(() => {
    const savedProductUrls = storageService.getProductImageUrls();
    return savedProductUrls || originalProductUrls;
  });
  
  // Create a virtual "Original Images" set 
  const originalImagesSet: ImageSet = useMemo(() => {
    console.log('Creating original images set with params');
    
    // Try to load saved parameters for the original set
    const savedOriginalHeroParams = storageService.getHeroParamsForSet(ORIGINAL_SET_ID);
    const savedOriginalProductParams = storageService.getProductParamsForSet(ORIGINAL_SET_ID);
    
    const heroParams = savedOriginalHeroParams || {...originalHeroParams};
    const productParams = savedOriginalProductParams || {...originalProductParams};
    
    console.log('Original set hero params:', heroParams);
    console.log('Original set product params:', productParams);
    
    return {
      id: ORIGINAL_SET_ID,
      name: "Original Images",
      heroUrl: originalHeroUrl,
      productUrls: originalProductUrls,
      heroParams,
      productParams
    };
  }, []);

  // Initialize image sets from localStorage
  const [imageSets, setImageSets] = useState<ImageSet[]>(() => {
    const savedImageSets = storageService.getImageSets();
    
    if (savedImageSets.length === 0) {
      console.log('No saved image sets found in localStorage');
      return [];
    }
    
    try {
      // Filter out any sets with the original set ID
      const userSets = savedImageSets.filter((set: ImageSet) => set.id !== ORIGINAL_SET_ID);
      
      // Ensure each set has all required properties
      const validatedSets = userSets.map((set: ImageSet) => {
        const validSet = {
          id: set.id || Date.now().toString(),
          name: set.name || 'Unnamed Set',
          heroUrl: set.heroUrl || originalHeroUrl,
          productUrls: Array.isArray(set.productUrls) ? set.productUrls : [...originalProductUrls],
          heroParams: set.heroParams && typeof set.heroParams === 'object' ? set.heroParams : {},
          productParams: set.productParams && typeof set.productParams === 'object' ? set.productParams : {}
        };
        
        // Also check if we have localStorage params for this set
        const storedHeroParams = storageService.getHeroParamsForSet(set.id);
        const storedProductParams = storageService.getProductParamsForSet(set.id);
        
        if (storedHeroParams) {
          validSet.heroParams = storedHeroParams;
          console.log(`Loaded stored hero params for set ${set.id}:`, validSet.heroParams);
        }
        
        if (storedProductParams) {
          validSet.productParams = storedProductParams;
          console.log(`Loaded stored product params for set ${set.id}:`, validSet.productParams);
        }
        
        return validSet;
      });
      
      console.log('Loaded validated image sets:', validatedSets);
      return validatedSets;
    } catch (e) {
      console.error('Error processing saved image sets, resetting to empty array:', e);
      return [];
    }
  });
  
  // Save URLs to localStorage whenever they change
  useEffect(() => {
    storageService.setHeroImageUrl(heroImageUrl);
  }, [heroImageUrl]);
  
  useEffect(() => {
    storageService.setProductImageUrls(productImageUrls);
  }, [productImageUrls]);
  
  // Save image sets to localStorage whenever they change
  useEffect(() => {
    storageService.setImageSets(imageSets);
  }, [imageSets]);
  
  // Update localStorage when currentImageSetId changes
  useEffect(() => {
    storageService.setCurrentImageSetId(currentImageSetId);
  }, [currentImageSetId]);
  
  // Store editor content for hero params
  const [heroEditorContent, setHeroEditorContent] = useState<{[key: string]: string}>(() => {
    const currentSetId = storageService.getCurrentImageSetId();
    if (currentSetId) {
      const storedEditorContent = storageService.getHeroEditorContentForSet(currentSetId);
      return storedEditorContent ? { [currentSetId]: storedEditorContent } : {};
    }
    return {};
  });
  
  // Store editor content for product params
  const [productEditorContent, setProductEditorContent] = useState<{[key: string]: string}>(() => {
    const currentSetId = storageService.getCurrentImageSetId();
    if (currentSetId) {
      const storedEditorContent = storageService.getProductEditorContentForSet(currentSetId);
      return storedEditorContent ? { [currentSetId]: storedEditorContent } : {};
    }
    return {};
  });
  
  // Save hero parameters to localStorage
  useEffect(() => {
    if (!heroImgixParams || typeof heroImgixParams !== 'object') {
      console.error('Invalid heroImgixParams:', heroImgixParams);
      return;
    }
    
    try {
      // Save hero params to localStorage for the current image set
      if (currentImageSetId === ORIGINAL_SET_ID) {
        // For the original set, maintain a global parameter state in localStorage
        storageService.setHeroImgixParams(heroImgixParams);
        storageService.setHeroParamsForSet(ORIGINAL_SET_ID, heroImgixParams);
      } else if (currentImageSetId) {
        // For custom sets, store parameters with the set ID
        storageService.setHeroParamsForSet(currentImageSetId, heroImgixParams);
      } else {
        // Fallback for no selected set
        storageService.setHeroImgixParams(heroImgixParams);
      }
      
      // Update image sets in state if a set is currently selected
      if (currentImageSetId) {
        console.log('Updating image set hero params:', currentImageSetId, heroImgixParams);
        
        if (currentImageSetId === ORIGINAL_SET_ID) {
          // Update the original set reference
          if (originalImagesSet) {
            originalImagesSet.heroParams = {...heroImgixParams};
          }
        } else {
          // Update the current image set with new parameters
          setImageSets(prevSets => {
            if (!Array.isArray(prevSets)) {
              console.error('Invalid prevSets is not an array:', prevSets);
              return [];
            }
            
            // Create a new array with updated parameters for the current set
            const updatedSets = prevSets.map(set => {
              if (!set || typeof set !== 'object') {
                console.error('Invalid set object:', set);
                return set;
              }
              
              return set.id === currentImageSetId 
                ? { ...set, heroParams: { ...heroImgixParams } }
                : set;
            });
            
            return updatedSets;
          });
        }
      }
    } catch (e) {
      console.error('Error saving hero parameters:', e);
    }
  }, [heroImgixParams, currentImageSetId]);
  
  // Save product parameters to localStorage
  useEffect(() => {
    if (!imgixParams || typeof imgixParams !== 'object') {
      console.error('Invalid imgixParams:', imgixParams);
      return;
    }
    
    try {
      // Save product params to localStorage for the current image set
      if (currentImageSetId === ORIGINAL_SET_ID) {
        // For the original set, maintain a global parameter state in localStorage
        storageService.setProductImgixParams(imgixParams);
        storageService.setProductParamsForSet(ORIGINAL_SET_ID, imgixParams);
      } else if (currentImageSetId) {
        // For custom sets, store parameters with the set ID
        storageService.setProductParamsForSet(currentImageSetId, imgixParams);
      } else {
        // Fallback for no selected set
        storageService.setProductImgixParams(imgixParams);
      }
      
      // Update image sets in state if a set is currently selected
      if (currentImageSetId) {
        console.log('Updating image set product params:', currentImageSetId, imgixParams);
        
        if (currentImageSetId === ORIGINAL_SET_ID) {
          // Update the original set reference
          if (originalImagesSet) {
            originalImagesSet.productParams = {...imgixParams};
          }
        } else {
          // Update the current image set with new parameters
          setImageSets(prevSets => {
            if (!Array.isArray(prevSets)) {
              console.error('Invalid prevSets is not an array:', prevSets);
              return [];
            }
            
            // Create a new array with updated parameters for the current set
            const updatedSets = prevSets.map(set => {
              if (!set || typeof set !== 'object') {
                console.error('Invalid set object:', set);
                return set;
              }
              
              return set.id === currentImageSetId 
                ? { ...set, productParams: { ...imgixParams } }
                : set;
            });
            
            return updatedSets;
          });
        }
      }
    } catch (e) {
      console.error('Error saving product parameters:', e);
    }
  }, [imgixParams, currentImageSetId]);
  
  // Handle hero parameter changes with editor content
  const handleHeroParamsChange = (params: any, editorContent?: string) => {
    console.log('Hero params changed:', params);
    console.log('Hero editor content changed:', editorContent);
    setHeroImgixParams(params);
    
    // If editor content is provided, save it
    if (editorContent && currentImageSetId) {
      setHeroEditorContent(prev => ({
        ...prev,
        [currentImageSetId]: editorContent
      }));
      
      // Store in localStorage
      storageService.setHeroEditorContentForSet(currentImageSetId, editorContent);
      
      // Also update the image set object in state if it's not the original set
      if (currentImageSetId !== ORIGINAL_SET_ID) {
        setImageSets(prevSets => 
          prevSets.map(set => 
            set.id === currentImageSetId 
              ? { ...set, heroEditorContent: editorContent }
              : set
          )
        );
      }
    }
  };
  
  // Handle product parameter changes with editor content
  const handleProductParamsChange = (params: any, editorContent?: string) => {
    console.log('Product params changed:', params);
    setImgixParams(params);
    
    // If editor content is provided, save it
    if (editorContent && currentImageSetId) {
      setProductEditorContent(prev => ({
        ...prev,
        [currentImageSetId]: editorContent
      }));
      
      // Store in localStorage
      storageService.setProductEditorContentForSet(currentImageSetId, editorContent);
      
      // Also update the image set object in state if it's not the original set
      if (currentImageSetId !== ORIGINAL_SET_ID) {
        setImageSets(prevSets => 
          prevSets.map(set => 
            set.id === currentImageSetId 
              ? { ...set, productEditorContent: editorContent }
              : set
          )
        );
      }
    }
  };
  
  // Handle saving a new image set
  const handleSaveImageSet = (newImageSet: Omit<ImageSet, 'id'>) => {
    console.log('Saving new image set with current parameters');
    
    const id = Date.now().toString();
    
    // Make sure we have non-empty parameters
    const heroParamsToSave = Object.keys(heroImgixParams || {}).length > 0 
      ? {...heroImgixParams}
      : {...originalHeroParams}; // Use original params as fallback
      
    const productParamsToSave = Object.keys(imgixParams || {}).length > 0
      ? {...imgixParams}
      : {...originalProductParams}; // Use original params as fallback
    
    console.log('Hero params for new set:', heroParamsToSave);
    console.log('Product params for new set:', productParamsToSave);
    
    // Get the current editor content
    const currentHeroEditorContent = heroEditorContent[currentImageSetId || 'default'];
    const currentProductEditorContent = productEditorContent[currentImageSetId || 'default'];
    
    const imageSet: ImageSet = {
      ...newImageSet,
      id,
      // Store current parameters with the image set
      heroParams: heroParamsToSave,
      productParams: productParamsToSave,
      // Store editor content
      heroEditorContent: currentHeroEditorContent,
      productEditorContent: currentProductEditorContent
    };
    
    setImageSets(prevSets => [...prevSets, imageSet]);
    
    // Set this as the current image set
    setCurrentImageSetId(id);
    
    // Store parameters in localStorage for this set
    storageService.setHeroParamsForSet(id, heroParamsToSave);
    storageService.setProductParamsForSet(id, productParamsToSave);
    
    // Store editor content for this set
    if (currentHeroEditorContent) {
      storageService.setHeroEditorContentForSet(id, currentHeroEditorContent);
    }
    if (currentProductEditorContent) {
      storageService.setProductEditorContentForSet(id, currentProductEditorContent);
    }
    
    console.log('New image set saved:', imageSet);
  };
  
  // Handle selecting an image set
  const handleSelectImageSet = (imageSet: ImageSet) => {
    console.log('Selecting image set:', imageSet);
    
    // Set the current image set ID
    setCurrentImageSetId(imageSet.id);
    
    // Update URLs
    setHeroImageUrl(imageSet.heroUrl);
    setProductImageUrls(imageSet.productUrls);
    
    // Look for editor content, prioritizing the imageSet object over localStorage
    const storedHeroEditorContent = storageService.getHeroEditorContentForSet(imageSet.id);
    const storedProductEditorContent = storageService.getProductEditorContentForSet(imageSet.id);
    
    // Update editor content state - prefer the content from the image set object
    setHeroEditorContent(prev => ({
      ...prev,
      [imageSet.id]: imageSet.heroEditorContent || storedHeroEditorContent || ''
    }));
    
    setProductEditorContent(prev => ({
      ...prev,
      [imageSet.id]: imageSet.productEditorContent || storedProductEditorContent || ''
    }));
    
    // If we have editor content in storage but not in the imageSet, update the imageSet
    if (!imageSet.heroEditorContent && storedHeroEditorContent && imageSet.id !== ORIGINAL_SET_ID) {
      // Update the image set with editor content from localStorage
      setImageSets(prevSets => 
        prevSets.map(set => 
          set.id === imageSet.id 
            ? { ...set, heroEditorContent: storedHeroEditorContent }
            : set
        )
      );
    }
    
    if (!imageSet.productEditorContent && storedProductEditorContent && imageSet.id !== ORIGINAL_SET_ID) {
      // Update the image set with editor content from localStorage
      setImageSets(prevSets => 
        prevSets.map(set => 
          set.id === imageSet.id 
            ? { ...set, productEditorContent: storedProductEditorContent }
            : set
        )
      );
    }
    
    // Check if we have saved parameters in localStorage for this specific set
    const savedHeroParams = imageSet.id !== ORIGINAL_SET_ID 
      ? storageService.getHeroParamsForSet(imageSet.id)
      : null;
      
    const savedProductParams = imageSet.id !== ORIGINAL_SET_ID 
      ? storageService.getProductParamsForSet(imageSet.id)
      : null;
    
    // Special handling for the original set
    if (imageSet.id === ORIGINAL_SET_ID) {
      console.log('Restoring original set parameters');
      
      // For original set, always use its stored parameters
      if (imageSet.heroParams && Object.keys(imageSet.heroParams).length > 0) {
        console.log('Setting hero params from original set:', imageSet.heroParams);
        setHeroImgixParams({...imageSet.heroParams});
      } else {
        console.log('Using default original hero params');
        setHeroImgixParams({...originalHeroParams});
      }
      
      if (imageSet.productParams && Object.keys(imageSet.productParams).length > 0) {
        console.log('Setting product params from original set:', imageSet.productParams);
        setImgixParams({...imageSet.productParams});
      } else {
        console.log('Using default original product params');
        setImgixParams({...originalProductParams});
      }
      
      return;
    }
    
    // For custom sets, restore hero parameters in order of priority:
    // 1. Local storage for this specific set
    // 2. Parameters stored in the image set object
    // 3. Empty object as fallback
    // Get current parameters to use as fallback instead of empty objects
    const currentHeroParams = heroImgixParams || {};
    const currentProductParams = imgixParams || {};
    
    let heroParamsToUse = currentHeroParams;
    let productParamsToUse = currentProductParams;
    
    // Try to get hero parameters in order of priority
    if (savedHeroParams) {
      // The storageService already returns parsed objects, no need to parse again
      if (typeof savedHeroParams === 'object' && Object.keys(savedHeroParams).length > 0) {
        console.log('Restoring hero params from localStorage:', savedHeroParams);
        heroParamsToUse = savedHeroParams;
      } else {
        console.log('localStorage hero params are empty, checking image set params');
        // Continue to next option
      }
    }
    
    // If we didn't get valid params from localStorage, check the image set
    if (heroParamsToUse === currentHeroParams && 
        imageSet.heroParams && 
        typeof imageSet.heroParams === 'object' && 
        Object.keys(imageSet.heroParams).length > 0) {
      console.log('Restoring hero params from image set:', imageSet.heroParams);
      heroParamsToUse = {...imageSet.heroParams};
    }
    
    // If both localStorage and image set params are empty, keep current params
    if (heroParamsToUse === currentHeroParams) {
      if (Object.keys(currentHeroParams).length > 0) {
        console.log('Using current hero params instead of empty ones:', currentHeroParams);
        // Save these params to the image set for future use
        if (imageSet.id !== ORIGINAL_SET_ID) {
          console.log('Saving current hero params to image set:', imageSet.id);
          storageService.setHeroParamsForSet(imageSet.id, currentHeroParams);
          
          // Update the image set in state
          setImageSets(prevSets => 
            prevSets.map(set => 
              set.id === imageSet.id 
                ? { ...set, heroParams: { ...currentHeroParams } }
                : set
            )
          );
        }
      } else {
        console.log('No hero params found anywhere, using empty object');
      }
    }
    
    // Set the hero parameters
    setHeroImgixParams(heroParamsToUse);
    
    // Same approach for product parameters
    if (savedProductParams) {
      // The storageService already returns parsed objects, no need to parse again
      if (typeof savedProductParams === 'object' && Object.keys(savedProductParams).length > 0) {
        console.log('Restoring product params from localStorage:', savedProductParams);
        productParamsToUse = savedProductParams;
      } else {
        console.log('localStorage product params are empty, checking image set params');
        // Continue to next option
      }
    }
    
    // If we didn't get valid params from localStorage, check the image set
    if (productParamsToUse === currentProductParams && 
        imageSet.productParams && 
        typeof imageSet.productParams === 'object' && 
        Object.keys(imageSet.productParams).length > 0) {
      console.log('Restoring product params from image set:', imageSet.productParams);
      productParamsToUse = {...imageSet.productParams};
    }
    
    // If both localStorage and image set params are empty, keep current params
    if (productParamsToUse === currentProductParams) {
      if (Object.keys(currentProductParams).length > 0) {
        console.log('Using current product params instead of empty ones:', currentProductParams);
        // Save these params to the image set for future use
        if (imageSet.id !== ORIGINAL_SET_ID) {
          console.log('Saving current product params to image set:', imageSet.id);
          storageService.setProductParamsForSet(imageSet.id, currentProductParams);
          
          // Update the image set in state
          setImageSets(prevSets => 
            prevSets.map(set => 
              set.id === imageSet.id 
                ? { ...set, productParams: { ...currentProductParams } }
                : set
            )
          );
        }
      } else {
        console.log('No product params found anywhere, using empty object');
      }
    }
    
    // Set the product parameters
    setImgixParams(productParamsToUse);
  };
  
  // Handle deleting an image set
  const handleDeleteImageSet = (imageSetId: string) => {
    console.log('Deleting image set:', imageSetId);
    
    // If this is the current set, clear the current set ID
    if (currentImageSetId === imageSetId) {
      setCurrentImageSetId(null);
    }
    
    // Remove the set from the array
    setImageSets(prevSets => prevSets.filter(set => set.id !== imageSetId));
    
    // Clean up localStorage entries for this set
    storageService.deleteImageSetData(imageSetId);
    
    // Clean up editor content state
    setHeroEditorContent(prev => {
      const newState = {...prev};
      delete newState[imageSetId];
      return newState;
    });
    
    setProductEditorContent(prev => {
      const newState = {...prev};
      delete newState[imageSetId];
      return newState;
    });
    
    console.log('Image set deleted');
  };
  
  // Handle updating an existing image set
  const handleUpdateImageSet = (updatedSet: ImageSet) => {
    console.log('Updating image set with current parameters');
    
    // Make sure we have non-empty parameters
    const heroParamsToSave = Object.keys(heroImgixParams || {}).length > 0 
      ? {...heroImgixParams}
      : (updatedSet.heroParams && Object.keys(updatedSet.heroParams).length > 0)
        ? {...updatedSet.heroParams} // Keep existing params if any
        : {...originalHeroParams}; // Use original params as last resort
      
    const productParamsToSave = Object.keys(imgixParams || {}).length > 0
      ? {...imgixParams}
      : (updatedSet.productParams && Object.keys(updatedSet.productParams).length > 0)
        ? {...updatedSet.productParams} // Keep existing params if any
        : {...originalProductParams}; // Use original params as last resort
    
    console.log('Hero params for updated set:', heroParamsToSave);
    console.log('Product params for updated set:', productParamsToSave);
    
    // Get the current editor content
    const currentHeroEditorContent = heroEditorContent[updatedSet.id] || 
                                    storageService.getHeroEditorContentForSet(updatedSet.id) || 
                                    updatedSet.heroEditorContent;
                                    
    const currentProductEditorContent = productEditorContent[updatedSet.id] || 
                                       storageService.getProductEditorContentForSet(updatedSet.id) || 
                                       updatedSet.productEditorContent;
    
    // Preserve the current parameters in the updated set
    const updatedSetWithParams = {
      ...updatedSet,
      heroParams: heroParamsToSave,
      productParams: productParamsToSave,
      // Store editor content
      heroEditorContent: currentHeroEditorContent,
      productEditorContent: currentProductEditorContent
    };
    
    // Update the image sets array
    setImageSets(prevSets => 
      prevSets.map(set => 
        set.id === updatedSet.id ? updatedSetWithParams : set
      )
    );
    
    // Make sure this is still the current set
    setCurrentImageSetId(updatedSet.id);
    
    // Update parameter storage in localStorage for this set
    storageService.setHeroParamsForSet(updatedSet.id, heroParamsToSave);
    storageService.setProductParamsForSet(updatedSet.id, productParamsToSave);
    
    // Store editor content for this set
    if (currentHeroEditorContent) {
      storageService.setHeroEditorContentForSet(updatedSet.id, currentHeroEditorContent);
    }
    if (currentProductEditorContent) {
      storageService.setProductEditorContentForSet(updatedSet.id, currentProductEditorContent);
    }
    
    console.log('Image set updated with params:', updatedSetWithParams);
  };
  
  // Export image sets to a JSON file
  const handleExportImageSets = () => {
    if (imageSets.length === 0) return;
    
    try {
      // Create a JSON blob with the sets data
      const jsonData = JSON.stringify(imageSets, null, 2);
      
      // Validate JSON before creating blob
      const testParse = JSON.parse(jsonData); // This ensures the JSON is valid
      
      const blob = new Blob([jsonData], { type: 'application/json' });
      
      // Create a download link and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'imgix-image-sets.json';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      console.log('Export successful');
    } catch (error) {
      console.error('Error exporting sets:', error);
      alert(t('export_error') || 'Error exporting image sets.');
    }
  };
  
  // Import image sets from a JSON file
  const handleImportImageSets = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check file extension
    if (!file.name.toLowerCase().endsWith('.json')) {
      alert(t('import_error') + ' Only .json files are supported.');
      event.target.value = '';
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = e.target?.result as string;
        let importedSets;
        
        // Check for common JSON formatting issues
        if (!jsonData.trim().startsWith('[') || !jsonData.trim().endsWith(']')) {
          console.error('JSON format error: Data doesn\'t appear to be an array');
          alert(t('import_error') + ' The file doesn\'t contain a JSON array. It should start with [ and end with ].');
          if (event.target) event.target.value = '';
          return;
        }
        
        try {
          importedSets = JSON.parse(jsonData);
        } catch (parseError) {
          console.error('JSON parse error:', parseError);
          
          // Provide more helpful error message with details
          let errorMsg = ' Invalid JSON format.';
          if (parseError instanceof Error) {
            errorMsg += ' Error: ' + parseError.message;
          }
          
          alert(t('import_error') + errorMsg);
          if (event.target) event.target.value = '';
          return;
        }
        
        // Validate the imported data has the expected structure
        if (!Array.isArray(importedSets)) {
          console.error('Import error: Data is not an array');
          alert(t('import_error') + ' Data is not an array of image sets.');
          if (event.target) event.target.value = '';
          return;
        }
        
        // Validate each image set
        for (let i = 0; i < importedSets.length; i++) {
          const set = importedSets[i];
          if (typeof set !== 'object' || set === null) {
            console.error(`Import error: Item at index ${i} is not an object`);
            alert(t('import_error') + ` Item at position ${i+1} is not a valid image set.`);
            if (event.target) event.target.value = '';
            return;
          }
          
          if (!('id' in set)) {
            console.error(`Import error: Item at index ${i} is missing 'id' property`);
            alert(t('import_error') + ` Item at position ${i+1} is missing 'id' property.`);
            if (event.target) event.target.value = '';
            return;
          }
          
          if (!('name' in set)) {
            console.error(`Import error: Item at index ${i} is missing 'name' property`);
            alert(t('import_error') + ` Item at position ${i+1} is missing 'name' property.`);
            if (event.target) event.target.value = '';
            return;
          }
          
          if (!('heroUrl' in set)) {
            console.error(`Import error: Item at index ${i} is missing 'heroUrl' property`);
            alert(t('import_error') + ` Item at position ${i+1} is missing 'heroUrl' property.`);
            if (event.target) event.target.value = '';
            return;
          }
          
          if (!('productUrls' in set)) {
            console.error(`Import error: Item at index ${i} is missing 'productUrls' property`);
            alert(t('import_error') + ` Item at position ${i+1} is missing 'productUrls' property.`);
            if (event.target) event.target.value = '';
            return;
          }
          
          if (!Array.isArray(set.productUrls)) {
            console.error(`Import error: 'productUrls' at index ${i} is not an array`);
            alert(t('import_error') + ` 'productUrls' at position ${i+1} is not an array.`);
            if (event.target) event.target.value = '';
            return;
          }
          
          // Validate heroParams and productParams if they exist (not required)
          if ('heroParams' in set && (typeof set.heroParams !== 'object' || set.heroParams === null)) {
            console.error(`Import error: 'heroParams' at index ${i} is not an object`);
            alert(t('import_error') + ` 'heroParams' at position ${i+1} is not a valid object.`);
            if (event.target) event.target.value = '';
            return;
          }
          
          if ('productParams' in set && (typeof set.productParams !== 'object' || set.productParams === null)) {
            console.error(`Import error: 'productParams' at index ${i} is not an object`);
            alert(t('import_error') + ` 'productParams' at position ${i+1} is not a valid object.`);
            if (event.target) event.target.value = '';
            return;
          }
        }
        
        // All validation passed, add the imported sets to the existing ones
        setImageSets(prevSets => [...prevSets, ...importedSets]);
        
        // Clear the input field so the same file can be selected again
        if (event.target) {
          event.target.value = '';
        }
      } catch (error) {
        console.error('Error importing sets:', error);
        alert(t('import_error'));
        
        // Clear the input field
        if (event.target) {
          event.target.value = '';
        }
      }
    };
    
    reader.readAsText(file);
  };
  
  // Handle resetting URLs and parameters to original values
  const handleResetUrls = () => {
    console.log('RESET: Resetting to original values');
    
    // Use the special Original Images set
    handleSelectImageSet(originalImagesSet);
    
    console.log('RESET: Complete. Using Original Images set.');
  };
  
  // Create a list of products based on the number of image URLs
  // This approach ensures all images in a custom set are displayed, even if more than the original product count
  const updatedProducts = productImageUrls.map((imageUrl, index) => {
    // Use existing product as template if available, otherwise use the first product as template
    const templateProduct = regularProducts[index] || regularProducts[0];
    return {
      ...templateProduct,
      // Generate a unique ID for products beyond the original list
      id: index < regularProducts.length ? templateProduct.id : `custom-product-${index}`,
      imageSrc: imageUrl,
      // Use simple "Product N" naming for all products
      name: `Product ${index + 1}`
    };
  });

  return (
    <div style={{ 
      backgroundColor: '#1e1e23',
      fontFamily: 'Plus Jakarta Sans, Arial, sans-serif'
    }}>
      {/* Mobile Menu */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileMenuOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-col overflow-y-auto pb-12 shadow-xl" style={{ backgroundColor: '#1e1e23' }}>
                <div className="flex px-4 pb-2 pt-5 justify-between items-center">
                  <a href="#" className="relative">
                    <img
                      className="h-8 w-auto"
                      src="https://jpblogtzk.imgix.net/imgix_logo.png"
                      alt="Imgix"
                    />
                    {/* Demo label */}
                    <div className="absolute -right-2 -bottom-2 text-white text-xs font-bold tracking-wide">
                      DEMO
                    </div>
                  </a>
                  <button
                    type="button"
                    className="-m-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="space-y-6 border-t border-gray-200 px-4 py-6">
                  <div className="flow-root">
                    <a href={language === 'ja' ? 'https://www.imgix.com/jp' : 'https://www.imgix.com'} className="-m-2 block p-2 font-medium text-white hover:text-gray-300">
                        Main Site
                    </a>
                  </div>
                  
                 
                </div>
                <div className="space-y-6 border-t border-gray-200 px-4 py-6">
                  <div className="flow-root">
                    <a href="https://dashboard.imgix.com/sign-in" className="block w-full px-4 py-2 font-medium text-center text-white border border-[#3a3a3a] bg-[#2a2a33] rounded-md hover:bg-[#35353f]">
                      {language === 'en' ? 'Log In/Sign Up' : 'ログイン/サインアップ'}
                    </a>
                  </div>
                  <div className="flow-root">
                    <a href="https://www.imgix.com/contact" className="block w-full px-4 py-2 font-medium text-center text-white border border-[#3a3a3a] rounded-md hover:bg-[#2a2a33]">
                      {language === 'en' ? 'Contact Us' : 'お問い合わせ'}
                    </a>
                  </div>
                  <div className="flow-root">
                    <div className="flex items-center p-2">
                      <GlobeAltIcon className="h-5 w-5 text-white mr-2" aria-hidden="true" />
                      <button 
                        onClick={toggleLanguage}
                        className="font-medium text-white hover:text-blue-300"
                      >
                        {language === 'en' ? '日本語に切り替え' : 'Switch to English'}
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Beam - positioned outside navbar wrapper to ensure it's visible */}
      <div className="beam w-full h-[10px]" style={{ 
        backgroundImage: 'linear-gradient(90deg, #1e7494, #55bc90 25%, #ffebb9 45%, #ffba00 75%, #ff6666)'
      }}></div>
      
      {/* Image URL Drawer */}
      <ImageUrlDrawer
        defaultHeroUrl={heroImageUrl}
        defaultProductUrls={productImageUrls}
        onHeroUrlChange={setHeroImageUrl}
        onProductUrlsChange={setProductImageUrls}
        imageSets={[originalImagesSet, ...imageSets]}
        onSaveImageSet={handleSaveImageSet}
        onUpdateImageSet={handleUpdateImageSet}
        onSelectImageSet={handleSelectImageSet}
        onDeleteImageSet={handleDeleteImageSet}
        onExportImageSets={handleExportImageSets}
        onImportImageSets={handleImportImageSets}
        currentImageSetId={currentImageSetId}
      />
      
      {/* Header with navbar-wrapper */}
      <div className="navbar-wrapper relative">
        <header className="navbar shadow-sm relative z-40" style={{ backgroundColor: '#1e1e23' }}>
          <div className="padding-global px-4 sm:px-6 lg:px-8">
            <div className="container-mw-1280 max-w-7xl mx-auto">
              <div className="padding-vertical py-3">
                <div className="nav-content-wrapper flex items-center justify-between h-[100px]">
                  {/* Logo */}
                  <a href="#" className="nav-logo-wrapper flex items-center relative">
                    <img
                      className="h-12 w-auto brightness-110"
                      src="https://jpblogtzk.imgix.net/imgix_logo.png?bg=transparent"
                      alt="Imgix"
                    />
                    {/* Demo label */}
                    <div className="absolute -right-2 -bottom-2 text-white text-xs font-bold tracking-wide">
                      DEMO
                    </div>
                  </a>
                  
                  {/* Desktop Navigation */}
                  <div className="nav-bg-wrapper hidden lg:block"></div>
                  <div className="nav-mobile-component hidden lg:flex flex-1 justify-between items-center ml-8">
                    <div className="nav-links-component flex space-x-4">
                      
                      <a href={language === 'ja' ? 'https://www.imgix.com/jp' : 'https://www.imgix.com'} className="text-white px-4 h-[48px] flex items-center rounded-md hover:bg-[#35353f]">
                        Main Site
                      </a>
                      
                    </div>
                    
                    {/* Nav Buttons - Contact & Login */}
                    <div className="nav-buttons-component flex items-center space-x-4">
                      <a href="https://www.imgix.com/contact" className="text-white border border-[#3a3a3a] px-4 h-[48px] flex items-center rounded-md text-sm font-medium hover:bg-[#2a2a33]">
                        Contact Us
                      </a>
                      <a href="https://dashboard.imgix.com/sign-in" className="text-white border border-[#3a3a3a] bg-[#2a2a33] px-4 h-[48px] flex items-center rounded-md text-sm font-medium hover:bg-[#35353f]">
                        Log In/Sign Up
                      </a>
                      
                      {/* Language Toggle Dropdown - Moved to the far right with ml-auto */}
                      <Popover className="relative ml-auto">
                        {({ open }) => (
                          <>
                            <Popover.Button className="group inline-flex items-center text-sm font-medium text-white border border-[#3a3a3a] px-3 h-[48px] rounded-md hover:bg-[#2a2a33] focus:outline-none">
                              <GlobeAltIcon className="h-5 w-5 text-white" aria-hidden="true" />
                            </Popover.Button>
                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-200"
                              enterFrom="opacity-0 translate-y-1"
                              enterTo="opacity-100 translate-y-0"
                              leave="transition ease-in duration-150"
                              leaveFrom="opacity-100 translate-y-0"
                              leaveTo="opacity-0 translate-y-1"
                            >
                              <Popover.Panel className="absolute right-0 z-10 mt-3 w-40 transform px-2 sm:px-0">
                                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                                  <div className="relative grid" style={{ backgroundColor: '#1e1e23' }}>
                                    <div 
                                      className="p-4 hover:bg-[#2a2a33] cursor-pointer transition duration-150"
                                      onClick={() => language === 'ja' && toggleLanguage()}
                                    >
                                      <p className={`text-sm font-medium ${language === 'en' ? 'text-blue-400' : 'text-white'}`}>
                                        English
                                        {language === 'en' && (
                                          <span className="ml-2 inline-flex items-center rounded-full bg-blue-900 px-2 py-0.5 text-xs font-medium text-blue-200">
                                            Active
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                    <div 
                                      className="p-4 hover:bg-[#2a2a33] cursor-pointer transition duration-150"
                                      onClick={() => language === 'en' && toggleLanguage()}
                                    >
                                      <p className={`text-sm font-medium ${language === 'ja' ? 'text-blue-400' : 'text-white'}`}>
                                        日本語
                                        {language === 'ja' && (
                                          <span className="ml-2 inline-flex items-center rounded-full bg-blue-900 px-2 py-0.5 text-xs font-medium text-blue-200">
                                            現在
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </Popover.Panel>
                            </Transition>
                          </>
                        )}
                      </Popover>
                    </div>
                  </div>
                  
                  {/* Mobile menu button */}
                  <div className="mobile-menu-button lg:hidden">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-md p-2 text-white border border-[#3a3a3a] hover:bg-[#2a2a33]"
                      onClick={() => setMobileMenuOpen(true)}
                    >
                      <span className="sr-only">Open menu</span>
                      <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>
      </div>


      {/* Main Content */}
      <main>

          {/* Hero Section */}
          {heroProduct && (
            <section className="relative w-full" style={{ height: '600px' }}>
              {/* コンテナは画面全体の幅に広げる */}
              <ImgixMedia
                src={heroImageUrl}
                width={1920}         // 例として生成する画像の横幅（必要に応じて調整）
                height={600}         // 生成する画像の高さ
                alt={heroProduct.imageAlt || heroProduct.name}
                className="w-full h-full object-cover"
                imgixParams={heroImgixParams}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                <h1 className="text-4xl text-white">
                  {t('hero_title')}
                </h1>
              </div>
              
              {/* Add file size overlay for hero image */}
              <FileSizeOverlay
                originalUrl={heroImageUrl}
                optimizedUrl={`${heroImageUrl}?auto=format,compress`}
                isOptimized={'auto' in heroImgixParams}
                width={1920}
                height={600}
              />
            </section>
          )}


                {/* Content Section */}
                <section className="py-16" style={{ backgroundColor: '#1e1e23' }}>
                  <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-4xl font-extrabold text-white sm:text-5xl lg:text-6xl">
                      {t('imgix_demo')}
                    </h1>
                    <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-300">
                      {t('demo_desc1')}
                      <br className="hidden sm:block" />
                      {t('demo_desc2')}
                    </p>
                    <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-400">
                      {t('demo_desc3')}
                    </p>
                  </div>
                </section>

        {/* Product Grid */}
        <section>
          <ProductGrid 
            products={updatedProducts} 
            heroParams={heroImgixParams}
            heroOriginalCode={heroParamsCode}
            onHeroParamsChange={handleHeroParamsChange}
            productParams={imgixParams}
            onProductParamsChange={handleProductParamsChange}
            currentImageSetId={currentImageSetId}
          />
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#3a3a3a]" style={{ backgroundColor: '#1e1e23' }}>
        <h2 id="footer-heading" className="sr-only">Footer</h2>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="py-20">
            <div className="grid grid-cols-1 md:grid-flow-col md:auto-rows-min md:grid-cols-12 md:gap-x-8 md:gap-y-16">
              

              {/* Sitemap Sections */}
              <div className="col-span-6 mt-10 grid grid-cols-2 gap-8 sm:grid-cols-3 md:col-span-8 md:col-start-3 md:row-start-1 md:mt-0 lg:col-span-6 lg:col-start-2">
                <div className="grid grid-cols-1 gap-y-12 sm:col-span-2 sm:grid-cols-2 sm:gap-x-8">
                  <div>
                    <h3 className="text-sm font-medium text-white">{t('links')}</h3>
                    <ul role="list" className="mt-6 space-y-6">
                      {footerNavigation.links.map((item) => (
                        <li key={item.name} className="text-sm">
                          <a href={item.href} className="text-gray-400 hover:text-gray-300">
                            {item.name === 'Docs' ? t('docs') : 
                             item.name === 'GitHub' ? t('github') : item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-white">{t('company')}</h3>
                    <ul role="list" className="mt-6 space-y-6">
                      {footerNavigation.company.map((item) => (
                        <li key={item.name} className="text-sm">
                          <a href={item.href} className="text-gray-400 hover:text-gray-300">
                            {item.name === 'Who we are' ? t('who_we_are') : 
                             item.name === 'Press' ? t('press') : item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-white">{t('get_in_touch')}</h3>
                  <ul role="list" className="mt-6 space-y-6">
                    {footerNavigation.contact.map((item) => (
                      <li key={item.name} className="text-sm">
                        <a href={item.href} className="text-gray-400 hover:text-gray-300">
                          {t('contact')}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="py-10 text-center">
            <p className="text-sm text-gray-400">
              {t('copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
